import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "building-mobile-extensions"
    }}>{`Building mobile extensions`}</h1>
    <p>{`To support customer-specific features, and allow for the greatest flexibility, Adobe Experience Platform Mobile SDK provides an interface that allows customers to integrate with the Mobile Core at a much lower level. This interface allows customers to define extensions, which are similar in capabilities to the `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/experience-platform/tags/ui/extensions/overview.html"
      }}>{`tag extensions`}</a>{`. The Mobile Core allows all extensions to access the events and data being sent through the Experience Platform SDK.`}</p>
    <p>{`Extensions allow customers to extend the Experience Platform SDKs with their own code. This includes listening for and dispatching events, reading the shared state of any registered extension, and sharing the state of the current extension. The application can use the extension to monitor for information that Adobe does not expose by default. It can also use the extension to modify Experience Platform SDK internal operations. For example, the application can use the extension to add additional data to messages that are sent or by sending data to other systems.`}</p>
    <h2 {...{
      "id": "prerequisites-to-building-an-extension"
    }}>{`Prerequisites to building an extension`}</h2>
    <p>{`Before you build an extension, complete the following tasks:`}</p>
    <h3 {...{
      "id": "ensure-that-you-are-using-the-adobe-experience-platform-sdks"
    }}>{`Ensure that you are using the Adobe Experience Platform SDKs`}</h3>
    <p>{`  Extensions extend the behavior of these SDKs.`}</p>
    <h3 {...{
      "id": "ensure-that-you-can-accomplish-your-goals-only-by-using-an-extension"
    }}>{`Ensure that you can accomplish your goals only by using an extension`}</h3>
    <p>{`  To determine your goals, think about the following questions:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Do you need access to data that is not already exposed via the Adobe Experience Platform SDKs?`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Do you need to be notified when messages will be sent, or data is being collected by the Adobe Experience Platform SDKs?`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Do you need to add data to or modify data for outgoing messages?`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Do you need to expose data to other extensions or to rules processing?`}</p>
        <p parentName="li">{`If your answer to any of these questions is `}<strong parentName="p">{`yes`}</strong>{`, build the extension.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "review-the-namespace-conventions"
    }}>{`Review the namespace conventions`}</h3>
    <p>{`Components or data that are provided by Adobe must be clearly distinguished from the components or data that are provided by external parties. Inconsistent naming conventions impact module naming, event type, source names, and event data keys.`}</p>
    <p>{`The following naming rules apply for extensions:`}</p>
    <ul>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`ADOBE_PREFIX`}</inlineCode>{` is `}<inlineCode parentName="li">{`com.adobe`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`THIRDPARTY_PREFIX`}</inlineCode>{` is `}<inlineCode parentName="li">{`com.`}</inlineCode>{`. The prefix `}<inlineCode parentName="li">{`com.adobe.*`}</inlineCode>{` is reserved for Adobe.`}</li>
      <li parentName="ul">{`Third parties must prefix their extension name and any custom event types or sources they create with the `}<inlineCode parentName="li">{`THIRDPARTY_PREFIX`}</inlineCode>{` followed by their company name.`}</li>
      <li parentName="ul">{`By convention, Adobe will not prefix shared state keys or event data keys.  These names will be in the global namespace. For example: `}<inlineCode parentName="li">{`mid`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Adobe internal module names follow the pattern `}<inlineCode parentName="li">{`ADOBE_PREFIX.module.{moduleName}`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Adobe event types follow the pattern `}<inlineCode parentName="li">{`ADOBE_PREFIX.eventType.{eventType}`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Adobe event sources follow the pattern `}<inlineCode parentName="li">{`ADOBE_PREFIX.eventSource.{eventSource}`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Shared state `}<strong parentName="li">{`names`}</strong>{` (not `}<strong parentName="li">{`keys`}</strong>{`) must equal the module name.`}</li>
      <li parentName="ul">{`All constants will be named using `}<inlineCode parentName="li">{`lowerCamelCase`}</inlineCode>{`, and cases are normalized internally to make comparisons case-insensitive. For example, if you use `}<inlineCode parentName="li">{`Com.Adobe.moDule.AnAlytiCS`}</inlineCode>{` it will be internally converted to `}<inlineCode parentName="li">{`com.adobe.module.analytics`}</inlineCode>{`. An exception to this rule is that shared state names that are used in rules are compared in a case-sensitive manner. This means that when registering an extension, the actual case is retained internally, so that rule comparison can succeed.`}</li>
    </ul>
    <InlineAlert variant="warning" slots="text" mdxType="InlineAlert" />
    <p>{`You should use ASCII characters, even if your company name contains non-ASCII characters.`}</p>
    <h2 {...{
      "id": "get-started"
    }}>{`Get started`}</h2>
    <p>{`For help on building your own extension, please see:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/adobe/aepsdk-core-ios/blob/main/Documentation/EventHub/BuildingExtensions.md"
        }}>{`Building iOS Extension`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/adobe/aepsdk-core-android/blob/main/Documentation/EventHub/BuildingExtensions.md"
        }}>{`Building Android Extension`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://experienceleague.adobe.com/docs/experience-platform/tags/extension-dev/overview.html"
        }}>{`Building Tags Extension`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      